import type { AppVersion } from './types/AppVersion';

export type AppVersionStatus = {
  forceUpdate: AppVersion | null;
  liveUpdate: AppVersion | null;
};

export const NO_APP_VERSION_UPDATES: AppVersionStatus = { forceUpdate: null, liveUpdate: null };

export const compareVersions = (version1: string, version2: string): number => {
  const v1Parts = version1.split('.').map(Number);
  const v2Parts = version2.split('.').map(Number);

  for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
    const v1 = v1Parts[i] || 0;
    const v2 = v2Parts[i] || 0;

    if (v1 > v2) return 1;
    if (v1 < v2) return -1;
  }

  return 0;
};

/** Newest `appVersions` doc carrying each flag, as read from Firestore. */
export type AppVersionCandidates = {
  latestVersion?: AppVersion;
  forceUpdateVersion?: AppVersion;
};

/**
 * Pure decision layer for the two startup update questions.
 *
 * `installedVersion` is the running bundle's build-stamped version, so once an OTA
 * has been applied the live-update comparison is equal and nothing further is offered.
 *
 * The previous implementation compared against `currentVersion?.version || installedVersion`,
 * where `currentVersion` was the doc whose `version` equalled `installedVersion`. That lookup
 * could only ever hand back `installedVersion`, so it never changed the result — dropping it
 * is behaviour-preserving and removes the only reason the query needed full history.
 */
export const resolveAppVersionStatus = (
  installedVersion: string,
  { latestVersion, forceUpdateVersion }: AppVersionCandidates
): AppVersionStatus => ({
  forceUpdate:
    forceUpdateVersion && compareVersions(installedVersion, forceUpdateVersion.version) === -1
      ? forceUpdateVersion
      : null,
  liveUpdate:
    latestVersion && compareVersions(installedVersion, latestVersion.version) === -1 ? latestVersion : null,
});
